<template>
  <div>
    <!-- Hero banner -->
    <div class="keybox-hero mb-1">
      <div class="keybox-hero__bg"/>
      <div class="keybox-hero__content">
        <div>
          <h3 class="keybox-hero__title mb-25">{{ msg('Keyboxes') }}</h3>
          <p class="keybox-hero__subtitle mb-0">{{ msg('Manage key lockers for apartment and hotel room keys') }}</p>
        </div>
        <div class="d-flex flex-wrap mt-1" style="gap:10px">
          <div class="keybox-hero__stat">
            <feather-icon icon="LayersIcon" size="14"/>
            <span class="font-weight-bolder">{{ page.total }}</span> {{ msg('total') }}
          </div>
          <div class="keybox-hero__stat keybox-hero__stat--success">
            <feather-icon icon="CheckCircleIcon" size="14"/>
            <span class="font-weight-bolder">{{ onlineCount }}</span> {{ msg('online') }}
          </div>
          <div class="keybox-hero__stat keybox-hero__stat--secondary">
            <feather-icon icon="WifiIcon" size="14"/>
            <span class="font-weight-bolder">{{ offlineCount }}</span> {{ msg('offline') }}
          </div>
        </div>
      </div>
      <svg class="keybox-hero__illustration" viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="20" y="15" width="60" height="75" rx="6" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.25)" stroke-width="1.5"/>
        <rect x="30" y="28" width="12" height="12" rx="2" fill="rgba(255,255,255,.3)"/>
        <rect x="46" y="28" width="12" height="12" rx="2" fill="rgba(255,255,255,.25)"/>
        <rect x="62" y="28" width="12" height="12" rx="2" fill="rgba(255,255,255,.2)"/>
        <rect x="30" y="46" width="12" height="12" rx="2" fill="rgba(255,255,255,.25)"/>
        <rect x="46" y="46" width="12" height="12" rx="2" fill="rgba(255,255,255,.35)"/>
        <rect x="62" y="46" width="12" height="12" rx="2" fill="rgba(255,255,255,.2)"/>
        <rect x="30" y="64" width="12" height="12" rx="2" fill="rgba(255,255,255,.2)"/>
        <rect x="46" y="64" width="12" height="12" rx="2" fill="rgba(255,255,255,.25)"/>
        <rect x="62" y="64" width="12" height="12" rx="2" fill="rgba(255,255,255,.3)"/>
        <circle cx="75" cy="20" r="8" fill="rgba(255,255,255,.08)" class="keybox-hero__float1"/>
        <circle cx="10" cy="90" r="10" fill="rgba(255,255,255,.06)" class="keybox-hero__float2"/>
      </svg>
    </div>

    <!-- Filters -->
    <b-card no-body class="mb-1">
      <b-card-body class="py-75">
        <b-row class="align-items-end">
          <b-col cols="12" md="3" class="mb-75 mb-md-0">
            <b-form-group :label="msg('IP Address')" label-size="sm" class="mb-0">
              <b-form-input v-model="filter.like.ipAddress" size="sm" :placeholder="msg('192.168.0.10')"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3" class="mb-75 mb-md-0">
            <b-form-group :label="msg('Location')" label-size="sm" class="mb-0">
              <b-form-input v-model="filter.like.location" size="sm" :placeholder="msg('e.g. Lobby')"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="mb-75 mb-md-0">
            <b-form-group :label="msg('Status')" label-size="sm" class="mb-0">
              <v-select v-model="filter.in.status" :options="keyboxStatus" label="text" :reduce="s=>s.value"
                        :placeholder="msg('All')" multiple :clearable="true" class="select-size-sm"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="mb-75 mb-md-0">
            <b-form-group :label="msg('Installation')" label-size="sm" class="mb-0">
              <v-select v-model="filter.in.installationStatus" :options="installationStatuses" label="text" :reduce="s=>s.value"
                        :placeholder="msg('All')" multiple :clearable="true" class="select-size-sm"/>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="2" class="d-flex align-items-center" style="gap:8px">
            <b-button variant="primary" size="sm" @click="refresh">
              <feather-icon icon="SearchIcon" size="14" class="mr-25"/>{{ msg('Search') }}
            </b-button>
            <b-button variant="flat-secondary" size="sm" @click="clearFilters">
              <feather-icon icon="XIcon" size="14"/>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Table card -->
    <b-card no-body>
      <b-card-header class="d-flex align-items-center justify-content-between flex-wrap py-75">
        <div class="d-flex align-items-center" style="gap:12px">
          <h5 class="mb-0">{{ msg('Keyboxes') }}</h5>
          <b-badge variant="light-primary" pill>{{ page.total }} {{ msg('entries') }}</b-badge>
        </div>
        <div class="d-flex align-items-center" style="gap:8px">
          <div class="d-flex align-items-center" style="gap:6px">
            <label class="mb-0 small text-muted">{{ msg('Show') }}</label>
            <v-select v-model="page.amount" :options="[10, 25, 50, 100]" :clearable="false"
                      @option:selected="refresh" class="per-page-selector" style="width:70px"/>
            <label class="mb-0 small text-muted">{{ msg('entries') }}</label>
          </div>
          <b-button variant="primary" size="sm" @click="onNewKeybox">
            <feather-icon icon="PlusIcon" size="14" class="mr-25"/>{{ msg('New Keybox') }}
          </b-button>
        </div>
      </b-card-header>

      <b-card-body class="p-0">
        <b-table ref="keybox-table" striped hover responsive small
                 :items="keyboxes" :fields="tableFields" :busy="loading"
                 @row-clicked="onRowClick" class="mb-0 keybox-table"
                 :tbody-tr-class="row => row.status==='error' ? 'row-error' : ''">
          <template #table-busy>
            <div class="text-center py-4">
              <b-spinner variant="primary"/>
              <p class="mt-1 mb-0 text-muted small">{{ msg('Loading') }}...</p>
            </div>
          </template>

          <template #cell(device)="{ item }">
            <div class="d-flex align-items-center" style="gap:10px">
              <div class="keybox-table__thumb">
                <feather-icon icon="LayersIcon" size="20" class="text-primary"/>
                <span class="keybox-table__slots">{{ item.totalCompartments || '—' }}</span>
              </div>
              <div>
                <span class="font-weight-bolder d-block">{{ item.serialNumber || '—' }}</span>
                <small class="text-muted">{{ item.ipAddress || '—' }}</small>
              </div>
            </div>
          </template>

          <template #cell(buildingId)="{ value }">
            <b-link v-if="value" class="font-weight-500" @click.stop="routeToBuilding(value)">
              {{ buildings[value] ? buildings[value].address : value }}
            </b-link>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(location)="{ value }">
            <span class="text-truncate d-inline-block" style="max-width:120px" :title="value">
              {{ value || '—' }}
            </span>
          </template>

          <template #cell(status)="{ value }">
            <b-badge :variant="statusVariants[value] || 'secondary'" pill class="text-uppercase">
              {{ msg(value) }}
            </b-badge>
          </template>

          <template #cell(installationStatus)="{ value }">
            <b-badge variant="light-secondary" pill>{{ msg(value) }}</b-badge>
          </template>

          <template #cell(rooms)="{ item }">
            <div v-if="(item.realEstateIds||[]).length">
              <b-button size="sm" variant="link" class="p-0 text-primary" @click.stop="toggleVisibleRow(item.id)">
                {{ visibleRows[item.id] ? msg('Hide') : msg('Show') }} ({{ item.realEstateIds.length }})
              </b-button>
              <b-collapse :visible="visibleRows[item.id]">
                <ul class="list-unstyled mb-0 mt-50 small">
                  <li v-for="pid in item.realEstateIds" :key="pid" class="py-25">
                    {{ realEstates[pid] ? realEstates[pid].name : pid }}
                  </li>
                </ul>
              </b-collapse>
            </div>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex align-items-center" style="gap:4px">
              <b-button variant="flat-primary" size="sm" class="btn-icon" @click.stop="onRowClick(item)"
                        v-b-tooltip.hover="msg('Edit')">
                <feather-icon icon="EditIcon" size="14"/>
              </b-button>
              <b-button variant="flat-danger" size="sm" class="btn-icon"
                        @click.stop="openConfirmationModal('delete', item)"
                        v-b-tooltip.hover="msg('Delete')">
                <feather-icon icon="Trash2Icon" size="14"/>
              </b-button>
            </div>
          </template>
        </b-table>

        <div v-if="page.total > 0" class="d-flex align-items-center justify-content-between px-2 py-75 border-top">
          <small class="text-muted">{{ msg('Total') }} {{ page.total }}</small>
          <b-pagination v-model="page.currentPage" :total-rows="page.total" :per-page="page.amount"
                       size="sm" first-number last-number @input="refresh"/>
        </div>
      </b-card-body>
    </b-card>

    <!-- Modals -->
    <b-modal v-model="dialogs.newKeybox" size="lg" :hide-footer="true" no-close-on-backdrop centered
             body-class="p-0" header-class="pb-0">
      <template #modal-header>
        <h5 class="modal-title">{{ msg('New Keybox') }}</h5>
        <b-button variant="flat-secondary" size="sm" class="btn-icon" @click="dialogs.newKeybox=false">
          <feather-icon icon="XIcon"/>
        </b-button>
      </template>
      <keybox-panel ref="keyboxPanel" @submitted="onSubmitted"/>
    </b-modal>

    <b-modal v-model="dialogs.updateKeybox" size="xl" :hide-footer="true" no-close-on-backdrop centered
             body-class="p-0" header-class="pb-0">
      <template #modal-header>
        <h5 class="modal-title">{{ msg('Edit Keybox') }}</h5>
        <b-button variant="flat-secondary" size="sm" class="btn-icon" @click="dialogs.updateKeybox=false; selected=null">
          <feather-icon icon="XIcon"/>
        </b-button>
      </template>
      <edit-keybox-panel v-if="selected" ref="editKeyboxPanel" @submitted="onSubmitted" :id="String(selected)"/>
    </b-modal>

    <b-modal v-model="showConfirmationModal" :title="msg('Confirm Action')" centered
             :cancel-title="msg('Cancel')" ok-variant="danger" @ok="confirmAction" @cancel="showConfirmationModal=false">
      <p class="mb-0">{{ modalMessage }}</p>
    </b-modal>

    <!-- Keybox config error alert modal -->
    <b-modal v-model="dialogs.configError" centered hide-footer size="md"
             header-bg-variant="danger" header-text-variant="white"
             body-class="text-center py-4">
      <template #modal-header>
        <div class="d-flex align-items-center w-100">
          <feather-icon icon="AlertTriangleIcon" size="20" class="mr-75"/>
          <h5 class="modal-title mb-0 text-white">{{ msg('Configuration Required') }}</h5>
          <b-button variant="link" class="ml-auto p-0 text-white" @click="dialogs.configError=false">
            <feather-icon icon="XIcon" size="18"/>
          </b-button>
        </div>
      </template>
      <div>
        <div class="mb-2">
          <div class="rounded-circle bg-light-danger d-inline-flex align-items-center justify-content-center" style="width:64px;height:64px">
            <feather-icon icon="AlertTriangleIcon" size="28" class="text-danger"/>
          </div>
        </div>
        <h4 class="mb-75">{{ msg('Keybox Provider Not Configured') }}</h4>
        <p class="text-muted mb-2" style="max-width:380px;margin:0 auto">
          {{ configErrorMessage }}
        </p>
        <div class="bg-light rounded p-1 mb-2" style="max-width:400px;margin:0 auto">
          <p class="mb-50 font-weight-bold small">{{ msg('How to fix this') }}:</p>
          <ol class="text-left small mb-0 pl-2">
            <li class="mb-25">{{ msg('Go to') }} <b>{{ msg('Organization') }}</b> → <b>{{ msg('Branches') }}</b></li>
            <li class="mb-25">{{ msg('Edit the branch assigned to this keybox') }}</li>
            <li>{{ msg('Set the Keybox Provider (e.g. Quipeng) in the branch settings') }}</li>
          </ol>
        </div>
        <b-button variant="primary" @click="dialogs.configError=false">
          {{ msg('Understood') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import KeyboxPanel from './keyboxPanel.vue'
import EditKeyboxPanel from './editKeyboxPanel.vue'

export default {
  name: 'Keyboxes',
  components: { KeyboxPanel, EditKeyboxPanel },
  data() {
    return {
      loading: true,
      keyboxes: [],
      realEstates: {},
      selected: null,
      buildings: {},
      showConfirmationModal: false,
      modalMessage: '',
      modalEvent: '',
      selectedItem: null,
      visibleRows: {},
      filter: {
        like: { ipAddress: '', location: '' },
        in: { status: [], installationStatus: [] }
      },
      sort: { by: 'creationDate', direction: 'desc' },
      page: { amount: 10, currentPage: 1, total: 0 },
      configErrorMessage: '',
      dialogs: { newKeybox: false, updateKeybox: false, configError: false },
      installationStatuses: [
        { value: 'installed', text: 'Installed' },
        { value: 'pending', text: 'Pending' },
        { value: 'failed', text: 'Failed' }
      ],
      statusVariants: {
        online: 'success',
        offline: 'secondary',
        inProgress: 'warning',
        error: 'danger'
      }
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('keybox', ['allKeyboxStatus']),
    keyboxStatus() {
      return this.allKeyboxStatus.map(s => ({ value: s.value, text: this.msg(s.text) }))
    },
    tableFields() {
      return [
        { key: 'device', label: this.msg('Device'), thStyle: { width: '200px' } },
        { key: 'buildingId', label: this.msg('Building'), thStyle: { width: '140px' } },
        { key: 'location', label: this.msg('Location'), thStyle: { width: '120px' } },
        { key: 'status', label: this.msg('Status'), thStyle: { width: '100px' } },
        { key: 'installationStatus', label: this.msg('Installation'), thStyle: { width: '100px' } },
        { key: 'rooms', label: this.msg('Rooms'), thStyle: { width: '100px' } },
        { key: 'actions', label: '', thStyle: { width: '90px' }, tdClass: 'text-right' }
      ]
    },
    onlineCount() {
      return this.keyboxes.filter(k => k.status === 'online').length
    },
    offlineCount() {
      return this.keyboxes.filter(k => k.status === 'offline' || k.status === 'error').length
    }
  },
  created() {
    this.refresh()
    this.$root.$on('crm::submitBusinessUnit', (this._submitListener = () => {}))
  },
  beforeDestroy() {
    this.$root.$off('crm::submitBusinessUnit', this._submitListener)
  },
  methods: {
    ...mapActions('keybox', ['getAllKeyboxs', 'deleteService', 'getKeyboxById']),
    clearFilters() {
      this.filter = { like: { ipAddress: '', location: '' }, in: { status: [], installationStatus: [] } }
      this.refresh()
    },
    toggleVisibleRow(id) {
      this.$set(this.visibleRows, id, !this.visibleRows[id])
    },
    routeToBuilding(buildingId) {
      window.open(`/building/edit/${buildingId}`, '_blank')
    },
    refresh() {
      this.loading = true
      this.getAllKeyboxs({
        page: this.page.currentPage,
        amount: this.page.amount,
        filter: this.filter,
        sortcolumn: this.sort.by,
        sortby: this.sort.direction
      }).then(this.onKeyboxesRetrieve, () => { this.loading = false })
    },
    onKeyboxesRetrieve(data) {
      this.page = { amount: data.Amount, total: data.Total, currentPage: data.Page }
      this.keyboxes = data.Keyboxs || []
      this.realEstates = data.RealEstates || {}
      this.buildings = data.Buildings || {}
      this.loading = false
    },
    onNewKeybox() {
      this.dialogs.newKeybox = true
    },
    onRowClick(keybox) {
      this.loading = true
      this.getKeyboxById({ id: keybox.id })
        .then(() => {
          this.selected = null
          this.$nextTick(() => {
            this.selected = keybox.id
            this.dialogs.updateKeybox = true
          })
        })
        .catch(err => {
          const msg = (typeof err === 'string' ? err : (err && err.message) || '')
          if (msg.toLowerCase().includes('config') || msg.toLowerCase().includes('provider')) {
            this.configErrorMessage = msg
          } else {
            this.configErrorMessage = msg || this.msg('Could not load keybox. Please check branch configuration.')
          }
          this.dialogs.configError = true
        })
        .finally(() => { this.loading = false })
    },
    openConfirmationModal(event, item) {
      this.modalEvent = event
      this.selectedItem = item
      this.modalMessage = event === 'delete' ? this.msg('Are you sure you want to delete this keybox?') : ''
      this.showConfirmationModal = true
    },
    confirmAction() {
      if (this.modalEvent === 'delete' && this.selectedItem) {
        this.deleteService({ id: this.selectedItem.id }).then(() => this.refresh())
      }
      this.showConfirmationModal = false
    },
    onSubmitted() {
      this.refresh()
      this.dialogs.newKeybox = false
      this.dialogs.updateKeybox = false
      this.selected = null
    }
  }
}
</script>

<style scoped>
.keybox-hero {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  min-height: 110px;
}
.keybox-hero__bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #00cfe8 0%, #28c76f 50%, #00d4b5 100%);
  z-index: 0;
}
.keybox-hero__content {
  position: relative;
  z-index: 1;
  padding: 18px 24px;
}
.keybox-hero__title { color: #fff; font-weight: 700; }
.keybox-hero__subtitle { color: rgba(255,255,255,.8); font-size: .85rem; }
.keybox-hero__stat {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: .78rem;
  color: #fff;
  background: rgba(255,255,255,.2);
}
.keybox-hero__stat--success { background: rgba(40,199,111,.4); }
.keybox-hero__stat--secondary { background: rgba(255,255,255,.15); }
.keybox-hero__illustration {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  width: 90px;
  opacity: .6;
  z-index: 0;
  pointer-events: none;
}
.keybox-hero__float1 { animation: keyboxFloat 5s ease-in-out infinite; }
.keybox-hero__float2 { animation: keyboxFloat 7s ease-in-out 1.5s infinite; }
@keyframes keyboxFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
@media (max-width: 576px) { .keybox-hero__illustration { display: none; } }

.keybox-table__thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: rgba(115,103,240,.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.keybox-table__slots { font-size: 10px; font-weight: 600; color: var(--primary); }
.per-page-selector { min-width: 70px; }
</style>

<style>
.row-error td { background: rgba(234,84,85,.06) !important; }
.dark-layout .row-error td { background: rgba(234,84,85,.1) !important; }
.dark-layout .keybox-hero__bg {
  background: linear-gradient(135deg, #008a9a 0%, #1a9d5c 50%, #00a892 100%);
}
</style>
